import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { FincluCustomer, CustomerProcessStatus } from '@/pages/CustomerSupport/types';
import InputField from '@/stories/Library/Components/InputField/InputField';
import { MexStates } from '@/utils/maps';
import { formatDateTime, formatPhoneNumber, formatCurrency } from '@/utils/utils';

interface CustomerInformationSectionProps {
	selectedCustomer: FincluCustomer;
	isEditing: boolean;
	displayCustomer: Partial<FincluCustomer>;
	handleFieldChange: (field: keyof FincluCustomer, value: any) => void;
	statusLabels: Record<CustomerProcessStatus, string>;
	statusColors: Record<CustomerProcessStatus, string>;
	isExpanded: boolean;
}

const CustomerInformationSection: React.FC<CustomerInformationSectionProps> = ({
	selectedCustomer,
	isEditing,
	displayCustomer,
	handleFieldChange,
	statusLabels,
	statusColors,
	isExpanded: parentIsExpanded
}) => {
	const t = useTranslations('SupportClients');
	const [isExpanded, setIsExpanded] = useState(parentIsExpanded);

	useEffect(() => {
		setIsExpanded(parentIsExpanded);
	}, [parentIsExpanded]);

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
			<div
				className="flex items-center justify-between cursor-pointer mb-4"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<h2 className="text-lg font-semibold">{t('PersonalInformation')}</h2>
				{isExpanded ? (
					<ChevronUp size={20} className="text-gray-500" />
				) : (
					<ChevronDown size={20} className="text-gray-500" />
				)}
			</div>

			{isExpanded && (
				<>
					<div className="mb-5">
						<h3 className="text-md font-semibold mb-3">{t('PersonalInformation')}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								{isEditing ? (
									<InputField
										label={t('FirstName')}
										type="text"
										value={displayCustomer.names || ''}
										onChange={(e) => handleFieldChange('names', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">{t('FirstName')}</label>
										<p className="text-sm font-medium">{selectedCustomer.names || 'N/A'}</p>
									</>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('FirstLastName')}
										type="text"
										value={displayCustomer.first_last_name || ''}
										onChange={(e) => handleFieldChange('first_last_name', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">{t('FirstLastName')}</label>
										<p className="text-sm font-medium">
											{selectedCustomer.first_last_name || 'N/A'}
										</p>
									</>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('SecondLastName')}
										type="text"
										value={displayCustomer.second_last_name || ''}
										onChange={(e) => handleFieldChange('second_last_name', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">
											{t('SecondLastName')}
										</label>
										<p className="text-sm font-medium">
											{selectedCustomer.second_last_name || 'N/A'}
										</p>
									</>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('Email')}
										type="email"
										value={displayCustomer.email || ''}
										onChange={(e) => handleFieldChange('email', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">{t('Email')}</label>
										<p className="text-sm font-medium">{selectedCustomer.email || 'N/A'}</p>
									</>
								)}
							</div>
							<div>
								<label className="block text-sm text-gray-500 mb-1">{t('PhoneNumber')}</label>
								<p className="text-sm font-medium">
									{formatPhoneNumber(selectedCustomer.phone_number)}
								</p>
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('DateOfBirth')}
										type="date"
										value={displayCustomer.date_of_birth || ''}
										onChange={(e) => handleFieldChange('date_of_birth', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">{t('DateOfBirth')}</label>
										<p className="text-sm font-medium">
											{selectedCustomer.date_of_birth
												? new Date(selectedCustomer.date_of_birth).toLocaleDateString()
												: 'N/A'}
										</p>
									</>
								)}
							</div>
						</div>
					</div>

					<div className="mb-5">
						<h3 className="text-md font-semibold mb-3">{t('Location')}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								{isEditing ? (
									<InputField
										label={t('State')}
										type="text"
										value={displayCustomer.state || ''}
										onChange={(e) => handleFieldChange('state', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">{t('State')}</label>
										<p className="text-sm font-medium">
											{MexStates[selectedCustomer.state as keyof typeof MexStates] || 'N/A'}
										</p>
									</>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('City')}
										type="text"
										value={displayCustomer.city || ''}
										onChange={(e) => handleFieldChange('city', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">{t('City')}</label>
										<p className="text-sm font-medium">{selectedCustomer.city || 'N/A'}</p>
									</>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('MaritalStatus')}
										type="text"
										value={displayCustomer.marital_status || ''}
										onChange={(e) => handleFieldChange('marital_status', e.target.value)}
										size="default"
									/>
								) : (
									<>
										<label className="block text-sm text-gray-500 mb-1">{t('MaritalStatus')}</label>
										<p className="text-sm font-medium">
											{t(toTranslationKey(selectedCustomer.marital_status ?? '')) || 'N/A'}
										</p>
									</>
								)}
							</div>
						</div>
					</div>

					<div className="mb-5">
						<h3 className="text-md font-semibold mb-3">{t('FinancialInformation')}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								{isEditing ? (
									<InputField
										label={t('MonthlyPaymentCreditBureau')}
										type="number"
										value={displayCustomer.credit_bureau_monthly_payment?.toString() || ''}
										onChange={(e) =>
											handleFieldChange('credit_bureau_monthly_payment', parseFloat(e.target.value))
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">
											{t('MonthlyPaymentCreditBureau')}:
										</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_monthly_payment
												? formatCurrency(selectedCustomer.credit_bureau_monthly_payment)
												: 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('CreditBureauScore')}
										type="number"
										value={displayCustomer.credit_bureau_default_score?.toString() || ''}
										onChange={(e) =>
											handleFieldChange('credit_bureau_default_score', parseFloat(e.target.value))
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('CreditBureauScore')}:</label>
										<p className="text-sm font-medium">
											{(selectedCustomer.credit_bureau_default_score
												? selectedCustomer.credit_bureau_default_score * 100
												: 0
											).toFixed(2) + '%' || 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('OpenCurrentBalance')}
										type="number"
										value={displayCustomer.credit_bureau_open_current_balance?.toString() || ''}
										onChange={(e) =>
											handleFieldChange(
												'credit_bureau_open_current_balance',
												parseFloat(e.target.value)
											)
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('OpenCurrentBalance')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_open_current_balance
												? formatCurrency(selectedCustomer.credit_bureau_open_current_balance)
												: 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('OpenOverdueBalance')}
										type="number"
										value={displayCustomer.credit_bureau_open_overdue_balance?.toString() || ''}
										onChange={(e) =>
											handleFieldChange(
												'credit_bureau_open_overdue_balance',
												parseFloat(e.target.value)
											)
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('OpenOverdueBalance')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_open_overdue_balance
												? formatCurrency(selectedCustomer.credit_bureau_open_overdue_balance)
												: 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('ClosedMaximumBalance')}
										type="number"
										value={displayCustomer.credit_bureau_closed_maximum_balance?.toString() || ''}
										onChange={(e) =>
											handleFieldChange(
												'credit_bureau_closed_maximum_balance',
												parseFloat(e.target.value)
											)
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('ClosedMaximumBalance')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_closed_maximum_balance
												? formatCurrency(selectedCustomer.credit_bureau_closed_maximum_balance)
												: 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('TotalTrials')}
										type="number"
										value={displayCustomer.credit_bureau_total_trials?.toString() || ''}
										onChange={(e) =>
											handleFieldChange('credit_bureau_total_trials', parseInt(e.target.value))
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('TotalTrials')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_total_trials ?? 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('MonthsOverdue')}
										type="number"
										value={displayCustomer.credit_bureau_months_overdue?.toString() || ''}
										onChange={(e) =>
											handleFieldChange('credit_bureau_months_overdue', parseInt(e.target.value))
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('MonthsOverdue')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_months_overdue ?? 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('YearsInCreditBureau')}
										type="number"
										value={displayCustomer.credit_bureau_years_in_credit_bureau?.toString() || ''}
										onChange={(e) =>
											handleFieldChange(
												'credit_bureau_years_in_credit_bureau',
												parseInt(e.target.value)
											)
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('YearsInCreditBureau')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_years_in_credit_bureau ?? 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('UtilizationPrePandemic')}
										type="number"
										value={displayCustomer.credit_bureau_utilization_pre_pandemic?.toString() || ''}
										onChange={(e) =>
											handleFieldChange(
												'credit_bureau_utilization_pre_pandemic',
												parseFloat(e.target.value)
											)
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('UtilizationPrePandemic')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_utilization_pre_pandemic !== null
												? `${(selectedCustomer.credit_bureau_utilization_pre_pandemic * 100).toFixed(2)}%`
												: 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('LastSixMonthsInquiries')}
										type="number"
										value={
											displayCustomer.credit_bureau_last_six_months_inquiries?.toString() || ''
										}
										onChange={(e) =>
											handleFieldChange(
												'credit_bureau_last_six_months_inquiries',
												parseInt(e.target.value)
											)
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('LastSixMonthsInquiries')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_last_six_months_inquiries ?? 'N/A'}
										</p>
									</div>
								)}
							</div>
							<div>
								{isEditing ? (
									<InputField
										label={t('UtilizationPostPandemic')}
										type="number"
										value={
											displayCustomer.credit_bureau_utilization_post_pandemic?.toString() || ''
										}
										onChange={(e) =>
											handleFieldChange(
												'credit_bureau_utilization_post_pandemic',
												parseFloat(e.target.value)
											)
										}
										size="default"
									/>
								) : (
									<div className="flex justify-between">
										<label className="text-sm text-gray-500">{t('UtilizationPostPandemic')}:</label>
										<p className="text-sm font-medium">
											{selectedCustomer.credit_bureau_utilization_post_pandemic !== null
												? `${(selectedCustomer.credit_bureau_utilization_post_pandemic * 100).toFixed(2)}%`
												: 'N/A'}
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div>
						<h3 className="text-md font-semibold mb-3">{t('StatusInformation')}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm text-gray-500 mb-1">{t('CurrentStatus')}</label>
								{isEditing ? (
									<select
										value={displayCustomer.customer_process_status}
										onChange={(e) =>
											handleFieldChange(
												'customer_process_status',
												e.target.value as CustomerProcessStatus
											)
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
									>
										{Object.values(CustomerProcessStatus).map((status) => (
											<option key={status} value={status}>
												{t(toTranslationKey(statusLabels[status]))}
											</option>
										))}
									</select>
								) : (
									<span
										className="inline-block px-3 py-1 rounded-xl text-sm font-medium"
										style={{
											backgroundColor:
												statusColors[selectedCustomer.customer_process_status] + '20',
											color: statusColors[selectedCustomer.customer_process_status]
										}}
									>
										{t(
											toTranslationKey(statusLabels[selectedCustomer.customer_process_status] ?? '')
										)}
									</span>
								)}
							</div>
							<div>
								<label className="block text-sm text-gray-500 mb-1">{t('StatusSince')}</label>
								<p className="text-sm font-medium">
									{formatDateTime(selectedCustomer.current_status_entered_at || '')}
								</p>
							</div>
							<div>
								<label className="block text-sm text-gray-500 mb-1">{t('ApprovedAt')}</label>
								<p className="text-sm font-medium">
									{formatDateTime(selectedCustomer.approved_at || '')}
								</p>
							</div>
							<div>
								<label className="block text-sm text-gray-500 mb-1">{t('CreatedAt')}</label>
								<p className="text-sm font-medium">
									{formatDateTime(selectedCustomer.created_at || '')}
								</p>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default CustomerInformationSection;
